import { Node } from '@tiptap/core';
import { ReactNodeViewRenderer, NodeViewWrapper } from '@tiptap/react';
import { BlockMath, InlineMath } from 'react-katex';
import React, { useState, useRef, useEffect } from 'react';
import { Button, ButtonGroup, DropdownButton, Dropdown } from 'react-bootstrap';

// --- SHARED LATEX TOOLBAR ---
const LatexToolbar = ({ onInsert, isInline }) => {
    const handleInsert = (e, snippet, cursorOffset) => {
        e.preventDefault(); 
        onInsert(snippet, cursorOffset);
    };

    return (
        <div 
            // 🟢 FIX 1: Swapped overflowX for flex-wrap so dropdown menus don't get clipped!
            className={`d-flex flex-wrap gap-2 bg-light p-2 border rounded shadow-sm ${isInline ? 'position-absolute' : 'mb-2'}`} 
            style={{ 
                zIndex: 1050, 
                ...(isInline ? { bottom: '100%', left: '0', marginBottom: '5px' } : {}) 
            }}
            // 🟢 FIX 2: Blanket focus protection! Clicking anywhere in here won't blur the textarea.
            onMouseDown={(e) => e.preventDefault()}
        >
            {/* Core Basics */}
            <ButtonGroup size="sm">
                {/* 🟢 FIX 3: Because of the blanket protection, we can safely use normal onClick now */}
                <Button variant="outline-secondary" onClick={(e) => handleInsert(e, '\\frac{num}{den}', 6)}>x/y</Button>
                <Button variant="outline-secondary" onClick={(e) => handleInsert(e, '^{2}', 2)}>x²</Button>
                <Button variant="outline-secondary" onClick={(e) => handleInsert(e, '_{1}', 2)}>x₁</Button>
                <Button variant="outline-secondary" onClick={(e) => handleInsert(e, '\\sqrt{x}', 6)}>√x</Button>
            </ButtonGroup>

            {/* General Math & Calculus Dropdown */}
            <DropdownButton as={ButtonGroup} size="sm" variant="outline-secondary" title="Math" className="ms-1">
                <Dropdown.Item onClick={(e) => handleInsert(e, '\\pm ', 4)}>± Plus/Minus</Dropdown.Item>
                <Dropdown.Item onClick={(e) => handleInsert(e, '\\approx ', 8)}>≈ Approximately</Dropdown.Item>
                <Dropdown.Item onClick={(e) => handleInsert(e, '\\neq ', 5)}>≠ Not Equal</Dropdown.Item>
                <Dropdown.Divider />
                <Dropdown.Item onClick={(e) => handleInsert(e, '\\int_{a}^{b} ', 13)}>∫ Integral</Dropdown.Item>
                <Dropdown.Item onClick={(e) => handleInsert(e, '\\sum_{i=1}^{n} ', 15)}>Σ Summation</Dropdown.Item>
                <Dropdown.Item onClick={(e) => handleInsert(e, '\\partial ', 9)}>∂ Partial Deriv</Dropdown.Item>
                <Dropdown.Item onClick={(e) => handleInsert(e, '\\lim_{x \\to \\infty} ', 19)}>lim Limit</Dropdown.Item>
            </DropdownButton>

            {/* Thermodynamics & Engineering Dropdown */}
            <DropdownButton as={ButtonGroup} size="sm" variant="outline-secondary" title="Engineering" className="ms-1">
                <Dropdown.Item onClick={(e) => handleInsert(e, '\\Delta H ', 9)}>ΔH Enthalpy</Dropdown.Item>
                <Dropdown.Item onClick={(e) => handleInsert(e, '\\dot{m} ', 8)}>ṁ Mass Flow</Dropdown.Item>
                <Dropdown.Item onClick={(e) => handleInsert(e, '^\\circ ', 7)}>° Degree</Dropdown.Item>
                <Dropdown.Item onClick={(e) => handleInsert(e, '\\alpha ', 7)}>α Alpha</Dropdown.Item>
                <Dropdown.Item onClick={(e) => handleInsert(e, '\\pi ', 4)}>π Pi</Dropdown.Item>
                <Dropdown.Divider />
                <Dropdown.Item onClick={(e) => handleInsert(e, '\\text{}', 6)}>Text/Units (No Italics)</Dropdown.Item>
                <Dropdown.Item onClick={(e) => handleInsert(e, '\\mathbf{}', 8)}>Bold Math</Dropdown.Item>
            </DropdownButton>

            {/* Chemistry Dropdown */}
            <DropdownButton as={ButtonGroup} size="sm" variant="outline-primary" title="🧪 Chem" className="ms-1">
                <Dropdown.Item onClick={(e) => handleInsert(e, '\\rightarrow ', 12)}>→ Forward Rxn</Dropdown.Item>
                <Dropdown.Item onClick={(e) => handleInsert(e, '\\rightleftharpoons ', 19)}>⇌ Equilibrium</Dropdown.Item>
                <Dropdown.Item onClick={(e) => handleInsert(e, '\\xrightarrow{\\Delta} ', 14)}>xrightarrow (Heat)</Dropdown.Item>
                <Dropdown.Divider />
                <Dropdown.Item onClick={(e) => handleInsert(e, '_{(s)} ', 6)}>(s) Solid State</Dropdown.Item>
                <Dropdown.Item onClick={(e) => handleInsert(e, '_{(aq)} ', 7)}>(aq) Aqueous</Dropdown.Item>
                <Dropdown.Item onClick={(e) => handleInsert(e, '[\\text{}]', 7)}>[ ] Concentration</Dropdown.Item>
            </DropdownButton>

            {/* Medical & Stats Dropdown */}
            <DropdownButton as={ButtonGroup} size="sm" variant="outline-danger" title="⚕️ Med/Stats" className="ms-1">
                <Dropdown.Item onClick={(e) => handleInsert(e, '\\mu\\text{g}', 11)}>μg Micrograms</Dropdown.Item>
                <Dropdown.Item onClick={(e) => handleInsert(e, '\\text{ mmHg}', 12)}>mmHg Blood Pressure</Dropdown.Item>
                <Dropdown.Divider />
                <Dropdown.Item onClick={(e) => handleInsert(e, 'p < 0.05', 8)}>p &lt; 0.05 Significance</Dropdown.Item>
                <Dropdown.Item onClick={(e) => handleInsert(e, '\\chi^2 ', 7)}>χ² Chi-Square</Dropdown.Item>
                <Dropdown.Item onClick={(e) => handleInsert(e, '\\sigma ', 7)}>σ Standard Deviation</Dropdown.Item>
                <Dropdown.Item onClick={(e) => handleInsert(e, '\\mu ', 4)}>μ Population Mean</Dropdown.Item>
            </DropdownButton>

            {/* IT & Computer Science Dropdown */}
            <DropdownButton as={ButtonGroup} size="sm" variant="outline-dark" title="💻 IT" className="ms-1">
                <Dropdown.Item onClick={(e) => handleInsert(e, '\\mathcal{O}()', 12)}>𝓞(n) Big O Notation</Dropdown.Item>
                <Dropdown.Divider />
                <Dropdown.Item onClick={(e) => handleInsert(e, '\\land ', 6)}>∧ Logical AND</Dropdown.Item>
                <Dropdown.Item onClick={(e) => handleInsert(e, '\\lor ', 5)}>∨ Logical OR</Dropdown.Item>
                <Dropdown.Item onClick={(e) => handleInsert(e, '\\oplus ', 7)}>⊕ XOR</Dropdown.Item>
                <Dropdown.Item onClick={(e) => handleInsert(e, '\\neg ', 5)}>¬ Logical NOT</Dropdown.Item>
                <Dropdown.Divider />
                <Dropdown.Item onClick={(e) => handleInsert(e, '\\in ', 4)}>∈ Element of</Dropdown.Item>
                <Dropdown.Item onClick={(e) => handleInsert(e, '\\subset ', 8)}>⊂ Subset</Dropdown.Item>
                <Dropdown.Item onClick={(e) => handleInsert(e, '\\cup ', 5)}>∪ Union</Dropdown.Item>
            </DropdownButton>
        </div>
    );
};

// --- SHARED INSERTION LOGIC ---
const useLatexInsertion = (inputRef, formula, updateFormula) => {
    return (snippet, cursorOffset) => {
        const input = inputRef.current;
        if (!input) return;

        const start = input.selectionStart;
        const end = input.selectionEnd;

        // Splice the snippet into the string at the cursor
        const newFormula = formula.substring(0, start) + snippet + formula.substring(end);
        updateFormula({ formula: newFormula });

        // Wait for React to render, then set the cursor perfectly inside the snippet
        setTimeout(() => {
            input.focus();
            input.setSelectionRange(start + cursorOffset, start + cursorOffset);
        }, 0);
    };
};

// --- 1. BLOCK MATH COMPONENT ---
const MathBlockComponent = (props) => {
    const [isEditing, setIsEditing] = useState(false);
    const inputRef = useRef(null);
    const formula = props.node.attrs.formula;

    const insertSnippet = useLatexInsertion(inputRef, formula, props.updateAttributes);

    useEffect(() => {
        if (isEditing && inputRef.current) inputRef.current.focus();
    }, [isEditing]);

    const handleChange = (e) => props.updateAttributes({ formula: e.target.value });

    return (
        <NodeViewWrapper className="math-block my-4">
            {isEditing ? (
                <div className="p-3 bg-white border border-primary rounded shadow-sm">
                    <LatexToolbar onInsert={insertSnippet} isInline={false} />
                    <textarea
                        ref={inputRef}
                        className="form-control font-monospace"
                        rows={3}
                        value={formula}
                        onChange={handleChange}
                        onBlur={() => setIsEditing(false)}
                        placeholder="Enter LaTeX formula here..."
                    />
                    <div className="text-muted small mt-2 fst-italic">Click anywhere outside this box to render the equation.</div>
                </div>
            ) : (
                <div 
                    className="p-4 bg-light border rounded text-center shadow-sm" 
                    style={{ overflowX: 'auto', cursor: 'pointer' }}
                    onClick={() => setIsEditing(true)}
                    title="Click to edit formula"
                >
                    {formula.trim() ? <BlockMath math={formula} settings={{ strict: false }} /> : <span className="text-muted">Empty Math Block (Click to edit)</span>}
                </div>
            )}
        </NodeViewWrapper>
    );
};

export const MathBlockExtension = Node.create({
    name: 'mathBlock',
    group: 'block',
    atom: true,
    addAttributes() {
        return {
            formula: {
                default: '',
                parseHTML: element => decodeURIComponent(element.getAttribute('data-formula') || ''),
            },
        };
    },
    parseHTML() { return [{ tag: 'div[data-type="math-block"]' }]; },
    renderHTML({ HTMLAttributes }) {
        return ['div', { 'data-type': 'math-block', 'data-formula': encodeURIComponent(HTMLAttributes.formula) }];
    },
    addNodeView() { return ReactNodeViewRenderer(MathBlockComponent); },
});

// --- 2. INLINE MATH COMPONENT ---
const MathInlineComponent = (props) => {
    const [isEditing, setIsEditing] = useState(false);
    const inputRef = useRef(null);
    const formula = props.node.attrs.formula;

    const insertSnippet = useLatexInsertion(inputRef, formula, props.updateAttributes);

    useEffect(() => {
        if (isEditing && inputRef.current) inputRef.current.focus();
    }, [isEditing]);

    const handleChange = (e) => props.updateAttributes({ formula: e.target.value });

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            setIsEditing(false);
        }
    };

    return (
        <NodeViewWrapper as="span" className="math-inline mx-1 position-relative">
            {isEditing ? (
                <>
                    <LatexToolbar onInsert={insertSnippet} isInline={true} />
                    <input
                        ref={inputRef}
                        type="text"
                        className="form-control form-control-sm font-monospace d-inline-block shadow-sm border-primary"
                        style={{ width: `${Math.max(formula.length + 2, 10)}ch`, minWidth: '150px' }}
                        value={formula}
                        onChange={handleChange}
                        onBlur={() => setIsEditing(false)}
                        onKeyDown={handleKeyDown}
                    />
                </>
            ) : (
                <span 
                    className="text-primary" 
                    style={{ cursor: 'pointer', display: 'inline-block', padding: '0 2px' }}
                    onClick={() => setIsEditing(true)}
                    title="Click to edit inline math"
                >
                    {formula.trim() ? <InlineMath math={formula} settings={{ strict: false }} /> : <span className="badge bg-light text-dark border">Empty</span>}
                </span>
            )}
        </NodeViewWrapper>
    );
};

export const MathInlineExtension = Node.create({
    name: 'mathInline',
    group: 'inline',
    inline: true,
    atom: true,
    addAttributes() {
        return {
            formula: {
                default: '',
                parseHTML: element => decodeURIComponent(element.getAttribute('data-formula') || ''),
            },
        };
    },
    parseHTML() { return [{ tag: 'span[data-type="math-inline"]' }]; },
    renderHTML({ HTMLAttributes }) {
        return ['span', { 'data-type': 'math-inline', 'data-formula': encodeURIComponent(HTMLAttributes.formula) }];
    },
    addNodeView() { return ReactNodeViewRenderer(MathInlineComponent); },
});